import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../lib/i18n'

/**
 * /welcome — Stripe Checkout success page. The trial subscription now exists
 * (no card taken). Hand the owner straight to the product's onboarding with
 * their email prefilled so they're live in minutes.
 */
type SessionInfo = { email: string | null; product: string; product_name: string; onboard_url: string; status: string }

export default function Welcome() {
  const { t } = useI18n()
  const [info, setInfo] = useState<SessionInfo | null>(null)
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading')

  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('session_id') ?? ''
    if (!id) {
      setState('error')
      return
    }
    fetch(`/api/billing/session?id=${encodeURIComponent(id)}`)
      .then((r) => (r.ok ? (r.json() as Promise<SessionInfo>) : Promise.reject(new Error('bad'))))
      .then((d) => {
        setInfo(d)
        setState('ok')
      })
      .catch(() => setState('error'))
  }, [])

  const onboard = info ? `${info.onboard_url}${info.email ? `?email=${encodeURIComponent(info.email)}` : ''}` : '#'

  return (
    <section className="section py-16 max-w-xl text-center">
      {state === 'loading' && <p className="text-ink-500">{t('Setting up your trial…')}</p>}
      {state === 'error' && (
        <>
          <h1 className="text-2xl font-bold text-white">{t('We could not find that session.')}</h1>
          <p className="mt-2 text-ink-500">{t('Start again from the pricing page — it takes 30 seconds.')}</p>
          <Link to="/pricing" className="btn-primary mt-6 inline-block">{t('Back to pricing')}</Link>
        </>
      )}
      {state === 'ok' && info && (
        <>
          <div className="text-5xl" aria-hidden>🎉</div>
          <h1 className="mt-4 text-3xl font-bold text-white">{t('Your 30-day free trial has started')}</h1>
          <p className="mt-3 text-ink-100">
            {t('No card was taken. We will only ask for one before the trial ends — you can stop any time.')}
          </p>
          <div className="card mt-8 text-left">
            <div className="text-xs font-semibold uppercase tracking-wider text-ink-500">{t('Next step')}</div>
            <p className="mt-2 text-white font-semibold">{t('Set up your')} {info.product_name} {t('in about 2 minutes')}</p>
            <p className="mt-1 text-sm text-ink-500">{info.email ? `${t('Account')}: ${info.email}` : ''}</p>
            <a href={onboard} className="btn-primary mt-4 block text-center">
              {t('Set up my')} {info.product_name} →
            </a>
          </div>
          <p className="mt-6 text-xs text-ink-500">
            {t('Questions?')} <a href="mailto:hello@myclienta.com" className="underline">hello@myclienta.com</a>
          </p>
        </>
      )}
    </section>
  )
}
